import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/layout/Layout";
import { SectionHeader } from "@/components/shared/SectionHeader";
import { ProcessStep } from "@/components/shared/ProcessStep";
import { 
  ArrowRight, 
  FileText, 
  Shield, 
  Clock,
  CheckCircle,
  Users,
  BarChart3,
} from "lucide-react";

const Inventory = () => {
  return (
    <Layout>
      {/* Hero */}
      <section className="section-padding bg-primary-light">
        <div className="container-wide">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block text-sm font-medium text-primary uppercase tracking-wider mb-3">
              The Foundation
            </span>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-6">
              The Leadership & Values Inventory
            </h1>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              A structured assessment that reveals individual leadership styles, values, 
              and priorities, providing the foundation for better partnership alignment 
              and performance.
            </p>
            <Link to="/start-inventory">
              <Button variant="hero" size="xl">
                Start the Inventory
                <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* What You'll Discover */}
      <section className="section-padding bg-background">
        <div className="container-wide">
          <SectionHeader
            eyebrow="Insights"
            title="What you'll discover"
            description="The Inventory provides structured insight into the leadership factors that matter most for partnership success."
          />

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
            <div className="card-premium text-center">
              <div className="w-14 h-14 rounded-2xl bg-primary-light flex items-center justify-center mx-auto mb-4">
                <Users className="h-7 w-7 text-primary" />
              </div>
              <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                Leadership Style
              </h3>
              <p className="text-sm text-muted-foreground">
                How you approach decisions, communication, and influencing others in the partnership context.
              </p>
            </div>
            <div className="card-premium text-center">
              <div className="w-14 h-14 rounded-2xl bg-primary-light flex items-center justify-center mx-auto mb-4">
                <Shield className="h-7 w-7 text-primary" />
              </div>
              <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                Core Values
              </h3>
              <p className="text-sm text-muted-foreground">
                The principles and priorities that drive your professional decisions and partnership expectations.
              </p>
            </div>
            <div className="card-premium text-center">
              <div className="w-14 h-14 rounded-2xl bg-primary-light flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="h-7 w-7 text-primary" />
              </div>
              <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                Governance Preferences
              </h3>
              <p className="text-sm text-muted-foreground">
                Your expectations around decision-making processes, accountability, and partnership structure.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding bg-secondary/30">
        <div className="container-wide">
          <SectionHeader
            eyebrow="Process"
            title="How the Inventory works"
          />

          <div className="max-w-3xl mx-auto space-y-8">
            <ProcessStep
              number={1}
              title="Complete online assessment"
              description="A focused questionnaire taking approximately 20-30 minutes. Answer honestly, there are no right or wrong responses."
            />
            <ProcessStep
              number={2}
              title="Receive your personal report"
              description="An automated, confidential report delivered immediately upon completion. Clear insights, actionable language."
            />
            <ProcessStep
              number={3}
              title="Use individually or as a foundation"
              description="Your report is valuable standalone, or becomes part of a partnership-wide analysis with Level 2 or 3 services."
            />
          </div>
        </div>
      </section>

      {/* Pilot Access */}
      <section className="section-padding bg-background">
        <div className="container-narrow">
          <div className="card-premium text-center">
            <span className="inline-block text-sm font-medium text-primary uppercase tracking-wider mb-3">
              Pilot Programme
            </span>
            <h2 className="font-display text-3xl font-semibold text-foreground mb-2">
              Free Access
            </h2>
            <p className="text-muted-foreground mb-6">
              Currently available at no cost during our pilot phase
            </p>
            
            <ul className="space-y-3 mb-8 max-w-md mx-auto text-left">
              {[
                "Comprehensive individual assessment",
                "Immediate automated report delivery",
                "Leadership style analysis",
                "Values and priorities identification",
                "Governance preference insights",
              ].map((feature, i) => (
                <li key={i} className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-foreground">{feature}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/start-inventory">
                <Button variant="hero" size="xl">
                  Start the Inventory
                </Button>
              </Link>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              Questions? <Link to="/contact" className="text-primary hover:underline">Get in touch</Link>
            </p>
          </div>
        </div>
      </section>

      {/* Privacy Assurance */}
      <section className="py-12 md:py-16 bg-primary-light">
        <div className="container-narrow text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Shield className="h-6 w-6 text-primary" />
            <span className="text-sm font-semibold text-primary uppercase tracking-wider">
              Complete Confidentiality
            </span>
          </div>
          <p className="text-lg text-foreground leading-relaxed max-w-2xl mx-auto">
            Your individual responses are <strong>never</strong> shared with other partners or your practice. 
            Personal reports are for your eyes only. Partnership reports contain only aggregated insights.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-background">
        <div className="container-narrow text-center">
          <Clock className="h-10 w-10 text-primary mx-auto mb-6" />
          <h2 className="font-display text-3xl font-semibold text-foreground mb-4">
            Ready in under 30 minutes
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
            Complete the Inventory at your own pace, and receive your personal leadership 
            insights immediately upon completion.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/start-inventory">
              <Button variant="hero" size="xl">
                Start the Inventory
                <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
            <Link to="/services">
              <Button variant="hero-outline" size="xl">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Inventory;
